import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Icon from "../AppIcon";
import NotificationPanel from "./NotificationPanel";
import { useAuth } from "../../contexts/AuthContext";
import {
  clusters as clustersAPI,
  projects as projectsAPI,
  regions as regionsAPI,
} from "../../lib/database";

const RoleBasedNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, user, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Get nav items based on specific role
  const getNavItems = () => {
    switch (userRole) {
      case "super_admin":
        return [
          {
            label: "Dashboard",
            path: "/host-dashboard",
            icon: "LayoutDashboard",
          },
          { label: "Admin", path: "/admin", icon: "Settings" },
          { label: "Regions", path: "/region-management", icon: "Building" },
          { label: "Clusters", path: "/cluster-management", icon: "MapPin" },
          { label: "Customers", path: "/customer-database", icon: "Users" },
          { label: "Approvals", path: "/approvals", icon: "CheckSquare" },
          { label: "Unworkable", path: "/unworkable", icon: "XCircle" },
        ];
      case "project_admin":
        return [
          {
            label: "Dashboard",
            path: "/host-dashboard",
            icon: "LayoutDashboard",
          },
          { label: "Regions", path: "/region-management", icon: "Building" },
          { label: "Clusters", path: "/cluster-management", icon: "MapPin" },
          { label: "Customers", path: "/customer-database", icon: "Users" },
          { label: "Approvals", path: "/approvals", icon: "CheckSquare" },
          { label: "Unworkable", path: "/unworkable", icon: "XCircle" },
        ];
      case "cluster_manager":
        return [
          {
            label: "Dashboard",
            path: "/cluster-dashboard",
            icon: "LayoutDashboard",
          },
          { label: "Customers", path: "/customer-database", icon: "Users" },
          { label: "Approvals", path: "/approvals", icon: "CheckSquare" },
          { label: "Unworkable", path: "/unworkable", icon: "XCircle" },
        ];
      case "worker":
        return [
          {
            label: "Dashboard",
            path: "/worker-dashboard",
            icon: "LayoutDashboard",
          },
        ];
      default:
        return [];
    }
  };

  const navItems = getNavItems();
  const isSuperAdmin = userRole === "super_admin";
  const isProjectAdmin = userRole === "project_admin";
  const isAdmin = isSuperAdmin || isProjectAdmin;

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      // Clear all user-specific localStorage keys
      localStorage.removeItem("userRole");
      localStorage.removeItem("selectedCluster");
      localStorage.removeItem("selectedProject");
      localStorage.removeItem("selectedRegion");
      localStorage.removeItem("installationComplete");
      localStorage.removeItem("completionDate");
      localStorage.removeItem("selectedCustomer");

      await signOut();
      setIsMobileMenuOpen(false);
    } catch (error) {
      console.error("Logout error:", error);
      // Still navigate to login even if signOut fails
      navigate("/login");
      setIsMobileMenuOpen(false);
    }
  };

  const isActive = (path) => location?.pathname === path;

  // Format role name for display
  const formatRoleName = (role) => {
    const roleMap = {
      super_admin: "Super Admin",
      project_admin: "Project Admin",
      cluster_manager: "Cluster Manager",
      worker: "Worker",
    };
    return (
      roleMap[role] ||
      role?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) ||
      "User"
    );
  };

  // Get role chip color
  const getRoleChipColor = (role) => {
    const colorMap = {
      super_admin:
        "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
      project_admin:
        "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      cluster_manager:
        "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      worker:
        "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    };
    return (
      colorMap[role] ||
      "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
    );
  };

  const userDisplayName =
    user?.name || user?.full_name || user?.username || "User";
  const userDisplayId = user?.unique_id || user?.user_id || user?.id || "";

  return (
    <>
      <nav className="role-based-nav">
        <div className="role-based-nav-logo">
          <div className="role-based-nav-logo-icon">
            <img
              src="/assets/images/brand.png"
              alt="Valence Energies logo featuring stylized 've' monogram in vibrant green color with modern three-dimensional design"
              className="w-6 h-6 object-contain"
            />
          </div>
          <span className="role-based-nav-logo-text">Valence Energies</span>
          {userRole && (
            <span
              className={`hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleChipColor(
                userRole,
              )}`}
            >
              {formatRoleName(userRole)}
            </span>
          )}
        </div>

        <div className="role-based-nav-menu">
          {navItems?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`role-based-nav-item ${
                isActive(item?.path) ? "active" : ""
              }`}
            >
              {item?.label}
            </button>
          ))}
        </div>

        <div className="role-based-nav-actions">
          <NotificationPanel />
          {isSuperAdmin && <ProjectSelector />}
          {user && (
            <div className="hidden sm:flex flex-col items-end pr-2">
              <span
                className="text-xs font-medium text-foreground leading-none max-w-[160px] truncate"
                title={userDisplayName}
              >
                {userDisplayName}
              </span>
              {userDisplayId && (
                <span className="text-[11px] text-muted-foreground leading-none">
                  ID: {userDisplayId}
                </span>
              )}
            </div>
          )}
          <button
            onClick={handleLogout}
            className="role-based-nav-item"
            title="Logout"
          >
            <Icon name="LogOut" size={20} />
          </button>
        </div>
      </nav>
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="role-based-nav-mobile-toggle lg:hidden"
        aria-label="Toggle menu"
      >
        <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
      </button>
      {isMobileMenuOpen && (
        <div className="role-based-nav-mobile-menu">
          <div className="flex flex-col gap-2 flex-1">
            {/* Role Chip in Mobile Menu */}
            {userRole && (
              <div className="px-4 py-3 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={16} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {userDisplayName}
                    </p>
                    {userDisplayId && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        ID: {userDisplayId}
                      </p>
                    )}
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${getRoleChipColor(
                        userRole,
                      )}`}
                    >
                      {formatRoleName(userRole)}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {navItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`role-based-nav-mobile-item ${
                  isActive(item?.path) ? "active" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 pt-4 border-t border-border">
            <NotificationPanel />
            {isSuperAdmin && <ProjectSelector />}
            <button
              onClick={handleLogout}
              className="role-based-nav-mobile-item"
            >
              <div className="flex items-center gap-3">
                <Icon name="LogOut" size={20} />
                <span>Logout</span>
              </div>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Project Selector for Super Admin
const ProjectSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem("selectedProject");
    if (saved && saved !== "all") {
      setSelectedProject(saved);
    }
    fetchProjects();
  }, []);

  useEffect(() => {
    // Auto-select first project if none selected and projects are loaded
    if (projects.length > 0 && !selectedProject) {
      const saved = localStorage.getItem("selectedProject");
      if (!saved || saved === "all") {
        const firstProject = projects[0]?.value;
        setSelectedProject(firstProject);
        localStorage.setItem("selectedProject", firstProject);
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent("projectChanged"));
      }
    }
  }, [projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await projectsAPI.getAll();
      const projectsList = (projectsData || []).map((project) => ({
        value: project.id,
        label: project.name,
      }));
      setProjects(projectsList);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (value) => {
    setSelectedProject(value);
    localStorage.setItem("selectedProject", value);
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent("projectChanged"));
    setIsOpen(false);
  };

  const selectedLabel =
    projects?.find((p) => p?.value === selectedProject)?.label ||
    "Select Project";

  return (
    <>
      <div className="cluster-selector">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="cluster-selector-trigger"
        >
          <Icon name="Folder" size={16} />
          <span className="hidden sm:inline">{selectedLabel}</span>
          <Icon name="ChevronDown" size={16} />
        </button>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-[1050]"
              onClick={() => setIsOpen(false)}
            />
            <div className="cluster-selector-dropdown">
              {projects?.length > 0 ? (
                projects.map((project) => (
                  <button
                    key={project?.value}
                    onClick={() => handleSelect(project?.value)}
                    className={`cluster-selector-item ${
                      selectedProject === project?.value ? "active" : ""
                    }`}
                  >
                    {project?.label}
                  </button>
                ))
              ) : (
                <div className="p-3 text-sm text-muted-foreground text-center">
                  No projects yet
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

// Region Selector for Project Admin
const RegionSelector = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem("selectedRegion");
    if (saved && saved !== "all") {
      setSelectedRegion(saved);
    }
    fetchRegions();
  }, [user]);

  useEffect(() => {
    // Auto-select first region if none selected and regions are loaded
    if (regions.length > 0 && !selectedRegion) {
      const saved = localStorage.getItem("selectedRegion");
      if (!saved || saved === "all") {
        setSelectedRegion(regions[0]?.value);
        localStorage.setItem("selectedRegion", regions[0]?.value);
      }
    }
  }, [regions]);

  const fetchRegions = async () => {
    try {
      setLoading(true);
      // Project admin should only see regions from their assigned project
      let regionsData = [];
      if (user?.project_id) {
        regionsData = await regionsAPI.getByProjectId(user.project_id);
      } else {
        // If no project assigned, show all regions (fallback)
        regionsData = await regionsAPI.getAll();
      }

      const regionsList = (regionsData || []).map((region) => ({
        value: region.id,
        label: region.name,
      }));
      setRegions(regionsList);
    } catch (err) {
      console.error("Error fetching regions:", err);
      setRegions([{ value: "all", label: "All Regions" }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (value) => {
    setSelectedRegion(value);
    localStorage.setItem("selectedRegion", value);
    setIsOpen(false);
  };

  const selectedLabel =
    regions?.find((r) => r?.value === selectedRegion)?.label || "Select Region";

  return (
    <div className="cluster-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="cluster-selector-trigger"
      >
        <Icon name="Building" size={16} />
        <span className="hidden sm:inline">{selectedLabel}</span>
        <Icon name="ChevronDown" size={16} />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[1050]"
            onClick={() => setIsOpen(false)}
          />
          <div className="cluster-selector-dropdown">
            {regions?.map((region) => (
              <button
                key={region?.value}
                onClick={() => handleSelect(region?.value)}
                className={`cluster-selector-item ${
                  selectedRegion === region?.value ? "active" : ""
                }`}
              >
                {region?.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RoleBasedNavigation;
